// src/components/LiveJobFeed.js
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import { 
  collection, 
  query, 
  onSnapshot, 
  orderBy,
  doc,
  deleteDoc,
  addDoc,
  Timestamp,
  serverTimestamp 
} from "firebase/firestore";
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Menu,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Divider,
  ListItemButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PendingIcon from "@mui/icons-material/Pending";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WorkOffIcon from "@mui/icons-material/WorkOff";
import { getStatusColor, getStatusIcon } from "../utils/statusHelpers";
import { formatDate, formatTime } from "../utils/formatHelpers";

import EngineerStatusCards from "./EngineerStatusCards";
import UpcomingJobsCalendar from "./UpcomingJobsCalendar";

function LiveJobFeed({ onJobSelect }) {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  // Action menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, job: null });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load jobs
  useEffect(() => {
    const jobsQuery = query(collection(db, "jobs"), orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      jobsQuery,
      (querySnapshot) => {
        const jobsList = [];
        querySnapshot.forEach((doc) => {
          jobsList.push({ id: doc.id, ...doc.data() });
        });
        setJobs(jobsList);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching jobs:", error);
        setIsLoading(false);
        showSnackbar("Error loading jobs", "error");
      }
    );

    return () => unsubscribe();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Filter and sort jobs
  const filteredJobs = useMemo(() => {
    let filtered = [...jobs];

    // Status filter
    if (statusFilter === "active") {
      filtered = filtered.filter(
        (job) => !job.status || !["Completed", "Incomplete"].includes(job.status)
      );
    } else if (statusFilter === "completed") {
      filtered = filtered.filter((job) => job.status === "Completed");
    } else if (statusFilter !== "all") {
      filtered = filtered.filter((job) => job.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.jobTemplateName?.toLowerCase().includes(search) ||
          job.customerName?.toLowerCase().includes(search) ||
          job.vehicleReg?.toLowerCase().includes(search) ||
          job.status?.toLowerCase().includes(search) ||
          job.engineerName?.toLowerCase().includes(search)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
        case "oldest":
          return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
        case "scheduled":
          const aDate = a.scheduledDate || a.scheduledDateTime;
          const bDate = b.scheduledDate || b.scheduledDateTime;
          return (aDate?.seconds || 0) - (bDate?.seconds || 0);
        case "customer":
          return (a.customerName || "").localeCompare(b.customerName || "");
        default:
          return 0;
      }
    });

    return filtered;
  }, [jobs, searchTerm, statusFilter, sortBy]);

  // Statistics
  const stats = useMemo(() => {
    const total = jobs.length;
    const active = jobs.filter(
      (job) => !job.status || !["Completed", "Incomplete"].includes(job.status)
    ).length;
    const completed = jobs.filter((job) => job.status === "Completed").length;
    const pending = jobs.filter((job) => job.status === "Pending" || !job.status).length;

    return { total, active, completed, pending };
  }, [jobs]);

  // Handle action menu
  const handleMenuOpen = (event, job) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedJob(null);
  };

  // Handle view
  const handleView = () => {
    if (selectedJob && onJobSelect) {
      onJobSelect(selectedJob.id);
    }
    handleMenuClose();
  };

  // Handle delete
  const handleDeleteClick = () => {
    setDeleteDialog({ open: true, job: selectedJob });
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    const jobToDelete = deleteDialog.job;
    setDeleteDialog({ open: false, job: null });

    try {
      await deleteDoc(doc(db, "jobs", jobToDelete.id));
      showSnackbar("Job deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting job:", error);
      showSnackbar("Failed to delete job: " + error.message, "error");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, job: null });
  };

  // Handle duplicate
  const handleDuplicate = async () => {
    const jobToDuplicate = selectedJob;
    handleMenuClose();

    try {
      // Create a copy without the ID and timestamps
      const { id, createdAt, updatedAt, completionData, status, ...jobData } = jobToDuplicate;

      const newJob = {
        ...jobData,
        status: "Pending",
        createdAt: serverTimestamp(),
        // Clear completion-related fields
        completionData: null,
        travelStartTime: null,
        onSiteTime: null,
      };

      await addDoc(collection(db, "jobs"), newJob);
      showSnackbar("Job duplicated successfully", "success");
    } catch (error) {
      console.error("Error duplicating job:", error);
      showSnackbar("Failed to duplicate job: " + error.message, "error");
    }
  };

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Avatar
            sx={{
              bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              width: 56,
              height: 56,
            }}
          >
            <AssignmentIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Live Job Feed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Real-time updates and complete job management
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Statistics Dashboard */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Jobs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats.active}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Active Jobs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats.completed}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats.pending}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Engineer Status Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          👷 Engineer Status
        </Typography>
        <EngineerStatusCards />
      </Box>

      {/* Upcoming Jobs Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          📅 Upcoming Jobs
        </Typography>
        <UpcomingJobsCalendar />
      </Box>

      {/* Search and Filter Bar */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search jobs by name, customer, vehicle, engineer, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "rgba(15,23,42,0.6)",
            },
          }}
        />

        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(e, newFilter) => newFilter && setStatusFilter(newFilter)}
          size="small"
          sx={{ flexShrink: 0 }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="Pending">Pending</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => setSortBy(e.target.value)}
            startAdornment={<SortIcon fontSize="small" sx={{ mr: 1, ml: 1 }} />}
          >
            <MenuItem value="newest">Newest First</MenuItem>
            <MenuItem value="oldest">Oldest First</MenuItem>
            <MenuItem value="scheduled">By Schedule</MenuItem>
            <MenuItem value="customer">By Customer</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Result counter */}
      {searchTerm && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Showing {filteredJobs.length} of {jobs.length} jobs
        </Typography>
      )}

      {/* Live feed list */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}

        {!isLoading && jobs.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <WorkOffIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: "#e5e7eb" }}>
              No Jobs Yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Jobs will appear here once they're created
            </Typography>
          </Box>
        )}

        {!isLoading && filteredJobs.length === 0 && jobs.length > 0 && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <SearchIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              No Matching Jobs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try adjusting your search or filters
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
            >
              Clear Filters
            </Button>
          </Box>
        )}

        {!isLoading && filteredJobs.length > 0 && (
          <List disablePadding>
            {filteredJobs.map((job) => {
              const scheduledDate = formatDate(job.scheduledDate || job.scheduledDateTime);
              const scheduledTime = formatTime(job.scheduledDate || job.scheduledDateTime);

              return (
                <React.Fragment key={job.id}>
                  <ListItemButton
                    onClick={() => onJobSelect && onJobSelect(job.id)}
                    sx={{
                      py: 2,
                      px: 3,
                      transition: "all 0.2s",
                      "&:hover": {
                        bgcolor: "rgba(102, 126, 234, 0.08)",
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "#e5e7eb" }}
                        >
                          {job.jobTemplateName || "Untitled Job"}
                        </Typography>
                        <Chip
                          icon={getStatusIcon(job.status)}
                          label={job.status || "Pending"}
                          color={getStatusColor(job.status)}
                          size="small"
                        />
                      </Stack>

                      <Stack spacing={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          👤 Customer: {job.customerName || "N/A"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          🚗 Vehicle: {job.vehicleReg || "N/A"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          👷 Engineer: {job.engineerName || "Unassigned"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          📅 Scheduled: {scheduledDate}
                          {scheduledTime && ` @ ${scheduledTime}`}
                        </Typography>
                      </Stack>
                    </Box>

                    <IconButton
                      onClick={(e) => handleMenuOpen(e, job)}
                      sx={{
                        "&:hover": {
                          bgcolor: "rgba(102, 126, 234, 0.15)",
                        },
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          View Details
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          Duplicate Job
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Delete Job</Typography>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteIcon color="error" />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the job{" "}
            <strong>{deleteDialog.job?.jobTemplateName}</strong> for{" "}
            <strong>{deleteDialog.job?.customerName}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. All job data, photos, and completion
            information will be permanently deleted.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Job
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: "", severity: "success" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: "", severity: "success" })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LiveJobFeed;